import { Card, DynamicLink, SectionWrapper } from 'src/components/shared';
import BgGradient from 'src/images/Ellipse_2047.png';
import BgGradient2 from 'src/images/Ellipse_2048.png';
import BgGradient3 from 'src/images/Ellipse_2049.png';
import { FaArrowRightLong } from 'react-icons/fa6';
import { useState, useRef, useEffect } from 'react';
import { highlightBracketedText } from 'src/utils/utils';

type VerticalTab = {
  tabTitle: string;
  featureCardTitle: string;
  featureCardDescription: string;
  featureCardImage: { url: string; title: string };
  featureCardLinkText: string;
  featureCardLinkUrl: string;
  card1Title: string;
  card2Title: string;
  card3Title: string;
  card4Title: string;
  card1Description: string;
  card2Description: string;
  card3Description: string;
  card4Description: string;
  card1Image: { url: string };
  card2Image: { url: string };
  card3Image: { url: string };
  card4Image: { url: string };
  videoFile2: { url: string };
  videoFile3: { url: string };
};

type CapabilityComponentProps = {
  content?: {
    title: string;
    linkButton: {
      buttonText: string;
      buttonLink: string;
    };
    verticalTabsCollection: {
      items: VerticalTab[];
    };
  };
  className?: string;
};



const CapabilityComponent = ({ content, className = '' }: CapabilityComponentProps) => {
  const [activeNav, setActiveNav] = useState('');
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isManualScroll = useRef(false);
  
  // Video states for each card and video position
  const [videoStates, setVideoStates] = useState<{
    [key: string]: { isPlaying: boolean; showVideo: boolean }
  }>({});

  const navItems = content?.verticalTabsCollection?.items.map(item => item.tabTitle) || [];

  useEffect(() => {
    if (navItems.length > 0 && !activeNav) {
      setActiveNav(navItems[0]);
    }
  }, [navItems, activeNav]);

  const checkVisibility = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const topThreshold = windowHeight * 0.3; // 30% from the top
    const bottomThreshold = windowHeight * 0.7; // 70% from the top

    return rect.top <= topThreshold && rect.bottom >= topThreshold;
  };

  const handleScroll = () => {
    if (!cardRefs.current.length || isManualScroll.current) return;

    for (let i = 0; i < cardRefs.current.length; i++) {
      const card = cardRefs.current[i];
      if (card && checkVisibility(card)) {
        setActiveNav(navItems[i]);
        break;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  console.log('CapabilityComponent rendered with content:', content);

  const scrollToCard = (index: number) => {
    const card = cardRefs.current[index];
    if (!card) return;

    isManualScroll.current = true;
    setActiveNav(navItems[index]);

    const offset = window.innerHeight * 0.3;
    const cardPosition = card.getBoundingClientRect().top + window.scrollY;
    const scrollTarget = cardPosition - offset;

    window.scrollTo({
      top: scrollTarget,
      behavior: 'smooth',
    });

    setTimeout(() => {
      isManualScroll.current = false;
    }, 1000);
  };

  // Helper function to check if URL is a video
  const isVideoUrl = (url: string): boolean => {
    return Boolean(url && (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov')));
  };

  // Helper function to get video URL for specific card position
  const getVideoUrl = (item: VerticalTab, position: 2 | 3) => {
    return position === 2 ? item.videoFile2?.url : item.videoFile3?.url;
  };

  // Helper function to get image URL for specific card position
  const getImageUrl = (item: VerticalTab, position: number) => {
    switch (position) {
      case 1: return item.card1Image?.url;
      case 2: return item.card2Image?.url;
      case 3: return item.card3Image?.url;
      case 4: return item.card4Image?.url;
      default: return '';
    }
  };

  // Function to toggle video play/pause
  const toggleVideoPlayPause = (cardIndex: number, position: 2 | 3) => {
    const item = content?.verticalTabsCollection?.items[cardIndex];
    if (!item) return;

    const videoUrl = getVideoUrl(item, position);
    if (!isVideoUrl(videoUrl)) return;

    const stateKey = `${cardIndex}-${position}`;
    const currentState = videoStates[stateKey] || { isPlaying: false, showVideo: false };

    if (!currentState.showVideo) {
      // First click - show video and start playing
      setVideoStates(prev => ({
        ...prev,
        [stateKey]: { isPlaying: true, showVideo: true }
      }));
      
      // Small timeout to ensure video element is rendered before playing
      setTimeout(() => {
        const videoElement = document.querySelector(`[data-video-id="${stateKey}"] video`) as HTMLVideoElement;
        if (videoElement) {
          videoElement.play();
        }
      }, 10);
    } else {
      // Video is already showing - toggle play/pause
      const videoElement = document.querySelector(`[data-video-id="${stateKey}"] video`) as HTMLVideoElement;
      
      if (videoElement) {
        if (currentState.isPlaying) {
          videoElement.pause();
          setVideoStates(prev => ({
            ...prev,
            [stateKey]: { ...prev[stateKey], isPlaying: false }
          }));
        } else {
          videoElement.play();
          setVideoStates(prev => ({
            ...prev,
            [stateKey]: { ...prev[stateKey], isPlaying: true }
          }));
        }
      }
    }
  };

  // Function to get media source (thumbnail or video)
  const getMediaSource = (item: VerticalTab, cardIndex: number, position: number) => {
    const imageUrl = getImageUrl(item, position);
    
    // Only positions 2 and 3 can have videos
    if (position === 2 || position === 3) {
      const videoUrl = getVideoUrl(item, position as 2 | 3);
      const stateKey = `${cardIndex}-${position}`;
      const currentState = videoStates[stateKey] || { isPlaying: false, showVideo: false };
      
      if (isVideoUrl(videoUrl)) {
        // If video exists and we're not showing it yet, use image as thumbnail
        return !currentState.showVideo ? imageUrl : videoUrl;
      }
    }
    
    // Default to image
    return imageUrl;
  };

  if (!content) return null;

  return (
    <SectionWrapper
      className={`grid-common-component-block capability pb-0 ${className}`}
      id="products"
    >
      <div className="container-fluid custom-spacer-col" style={{ position: 'relative' }}>
        <div className="main-content">
           {className !== 'event-verticaltab' && (
            <>
              {[BgGradient, BgGradient3, BgGradient2].map((gradient, index) => {
                const topPositions = [150, 550, 1300];
                return (
                  <img
                    key={`gradient${index + 1}`}
                    src={gradient}
                    style={{
                      zIndex: 1,
                      width: '100%',
                      height: '33.33%',
                      left: 0,
                      top: `${topPositions[index]}px`,
                      position: 'absolute',
                    }}
                    alt={`gradient${index + 1}`}
                  />
                );
              })}
             </>
          )}
          <div className="title-content">
            <span
              className="highlight-bracket-text"
              dangerouslySetInnerHTML={{
                __html: highlightBracketedText(content.title, 'bold') ?? '',
              }}
            />
            {content.linkButton?.buttonText?.trim() && (
              <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                <DynamicLink url={content.linkButton?.buttonLink} className="btn-white">
                  <span>{content.linkButton?.buttonText}</span>
                  <FaArrowRightLong className="btn-icon" />
                </DynamicLink>
              </div>
            )}
          </div>

          <div className="container-fluid" style={{ zIndex: 3, marginTop: '100px' }}>
            <div className="capability-wrapper">
              <div className="side-navigation" style={{ position: 'sticky', top: '20px' }}>
                {navItems.map((item, index) => (
                  <div
                    key={item}
                    className={`nav-item ${activeNav === item ? 'active' : ''}`}
                    onClick={() => scrollToCard(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    {item}
                  </div>
                ))}
              </div>
              <div className="cards-container">
                {content.verticalTabsCollection.items.map((item, index) => (
                  <div
                    key={index}
                    ref={el => {
                      cardRefs.current[index] = el;
                    }}
                    style={{ scrollMarginTop: '20px' }}
                  >
                    <Card
                      item={item}
                      index={index}
                      typographyClassName="fw-bold mb-0"
                      tabTitle={item.tabTitle}
                      className={className}
                      buttonText={content.linkButton?.buttonText}
                      buttonLink={content.linkButton?.buttonLink}
                      // Pass video-related props to Card component
                      videoStates={videoStates}
                      toggleVideoPlayPause={toggleVideoPlayPause}
                      getMediaSource={getMediaSource}
                      isVideoUrl={isVideoUrl}
                      getVideoUrl={getVideoUrl}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default CapabilityComponent;